<template>
  <div :class="device.isMobile ? 'py-2' : 'px-4 pb-2'">
    <v-row align="center" class="mb-4">
      <v-col cols="12" lg="auto" class="d-flex align-center">
        <report-food-select-food-companies></report-food-select-food-companies>
      </v-col>
      <v-col cols="4" lg="auto" class="d-flex align-center">
        <shift-picker v-model="selectedShifts" multiple />
      </v-col>
      <v-col cols="8" lg="auto" class="d-flex align-center">
        <autocomplete
          :label="$t('meal_categories')"
          v-model="selectedCategories"
          :items="categoryItems"
          item-text="text"
          item-value="value"
          multiple
          hide-details
        ></autocomplete>
      </v-col>
      <v-col cols="auto" class="d-flex align-center">
        <v-checkbox
          hide-details
          v-model="$store.state.report.filters.showCompanyNames"
          :label="$t('show_company_names_print')"
        ></v-checkbox>
      </v-col>
    </v-row>

    <grouped-shift-meals-table
      ref="mealsTable"
      :loading="loading"
      :daily-data="processedDailyData"
      :meal-categories="mealCategories"
      :category-labels="categoryLabels"
      :selected-shift="selectedShifts"
      :selected-categories="selectedCategories"
      :date-array="dateArray"
    />
  </div>
</template>

<script>
import reportService from "@/services/report";
import hasPrinting from "@/mixins/hasPrinting";
import ReportFoodSelectFoodCompanies from "@/components/report/fields/ReportFieldSelectFoodCompanies.vue";
import hasReportPrinting from "@/mixins/report/hasReportPrinting";
import hasExcel from "@/mixins/hasExcel";
import hasReloadWatch from "@/mixins/report/hasReloadWatch";
import GroupedShiftMealsTable from "@/components/report/shared/GroupedShiftMealsTable.vue";

export default {
  name: "ReportTabGroupMealsTabGroupedShiftMeals",
  mixins: [hasPrinting, hasReportPrinting, hasExcel, hasReloadWatch],
  components: {
    ReportFoodSelectFoodCompanies,
    GroupedShiftMealsTable,
  },
  data() {
    return {
      orders: [],
      selectedShifts: [1, 2, 3, 4], // Po default sve četiri smene
      selectedCategories: [], // Filter za kategorije jela
      loading: false, // Loader za učitavanje podataka
      lastRequestId: 0, // Ignoriši zastarele odgovore pri brzoj promeni filtera
    };
  },
  methods: {
    findUsers() {
      const companyIds = this.selectedFoodCompanies.map((company) => company.id).join(",");

      const requestId = ++this.lastRequestId;

      if (!this.filters.dates.from || !this.filters.dates.to || !companyIds) {
        this.orders = [];
        this.loading = false;
        return;
      }

      this.loading = true; // Počni loading

      const params = {
        date: this.filters.dates.from, // Prvi datum
        date2: this.filters.dates.to, // Drugi datum
        grouped: 1, // Jedan red po danu, smeni i grupi jela
      };
      reportService
        .getCompanyFoodKorisniciByDay(companyIds, params)
        .then((response) => {
          if (requestId !== this.lastRequestId) return;
          this.orders = response.data || [];
        })
        .catch(() => {
          if (requestId !== this.lastRequestId) return;
          this.orders = [];
        })
        .finally(() => {
          if (requestId === this.lastRequestId) this.loading = false; // Završi loading
        });
    },

    generateDateArray(startDate, endDate) {
      const dates = [];
      const start = new Date(startDate);
      const end = new Date(endDate);

      while (start <= end) {
        dates.push(start.toISOString().substr(0, 10));
        start.setDate(start.getDate() + 1);
      }

      return dates;
    },
  },
  computed: {
    selectedFoodCompanies() {
      return this.$store.state.report.selectedFoodCompanies;
    },
    selectedCompaniesNames() {
      return (this.selectedFoodCompanies || []).map((company) => company.name).join(", ");
    },
    // Grupe jela po category_id, redosled po category_order
    categoryList() {
      const byId = new Map();
      this.orders.forEach((row) => {
        if (!byId.has(row.category_id)) {
          byId.set(row.category_id, {
            id: row.category_id,
            name: row.category_name,
            order: row.category_order,
          });
        }
      });
      return [...byId.values()].sort((a, b) => a.order - b.order || a.id - b.id);
    },
    mealCategories() {
      return this.categoryList.map((category) => category.id);
    },
    categoryLabels() {
      return this.categoryList.reduce((acc, category) => {
        acc[category.id] = this.toLatin(category.name || "");
        return acc;
      }, {});
    },
    // { [datum]: { [`${category_id}_${smena}`]: količina } } - samo raspoređivanje, bez sabiranja
    processedDailyData() {
      const dailyData = {};
      this.dateArray.forEach((date) => {
        dailyData[date] = {};
      });
      this.orders.forEach((row) => {
        if (!dailyData[row.date]) return;
        dailyData[row.date][`${row.category_id}_${row.shift}`] = row.quantity;
      });
      return dailyData;
    },
    categoryItems() {
      return this.categoryList.map((category) => ({
        text: this.categoryLabels[category.id],
        value: category.id,
      }));
    },
    dateArray() {
      if (!this.filters.dates.from || !this.filters.dates.to) return [];
      return this.generateDateArray(this.filters.dates.from, this.filters.dates.to);
    },
    filters() {
      return this.$store.state.report.filters;
    },
    selectedCompanies() {
      return this.$store.state.report.selectedFoodCompanies;
    },
  },
  watch: {
    selectedFoodCompanies: {
      handler() {
        this.findUsers();
      },
      deep: true,
    },
    "filters.dates": {
      handler() {
        if (
          new Date(this.filters.dates.to).getTime() <
            new Date(this.filters.dates.from).getTime() ||
          !this.filters.dates.to
        ) {
          this.filters.dates.to = this.filters.dates.from;
        } else {
          this.findUsers();
        }
      },
      immediate: true,
      deep: true,
    },
    mealCategories: {
      handler(newCategories) {
        // Lista grupa se menja samo novim učitavanjem - selektuj sve
        this.selectedCategories = [...newCategories];
      },
      immediate: true,
    },
    isPrinting() {
      if (this.isPrinting) {
        const totalCompanies = this.$store.state.report.foodCompanies.length;
        const selectedCount = this.selectedCompanies.length;
        const allSelected = totalCompanies === selectedCount;

        // Subtitle with company count
        const companySummary = allSelected
          ? this.$t("for_all_companies")
          : `${this.$t("selected_companies")} (${selectedCount} ${this.$t(
              "of"
            )} ${totalCompanies})`;

        // Footer with company names
        let companiesFooter = "";
        if (this.filters.showCompanyNames) {
          const companyNames = this.selectedCompanies
            .map((company) => this.toLatin(company.name))
            .join(", ");
          companiesFooter = `<div style="margin-top: 20px; padding-top: 10px; border-top: 1px solid #ddd;">
            <strong>${this.$t("companies")}:</strong> ${companyNames}
          </div>`;
        }

        this.printDocument({
          tables: [
            {
              headers: this.$refs.mealsTable.printHeaders,
              items: this.$refs.mealsTable.printItems,
              showCaption: true,
            },
          ],
          title: [this.$t("report"), "Grupa jela"].join(": "),
          subtitle: `${this.subtitle}<h4 style="text-align: center">${companySummary}</h4>`,
          afterTableHtml: companiesFooter,
          hideDatetime: true,
        });
      }
    },
    isExcelDownloading() {
      if (this.isExcelDownloading) {
        this.downloadExcel({
          fileName: [this.$t("report"), "Grupa jela"].join(" ") + ".xlsx",
          sheets: [
            {
              name: "Grupa jela",
              headers: this.$refs.mealsTable.headers,
              items: this.$refs.mealsTable.printItems,
              title: [this.$t("report"), "Grupa jela"].join(" "),
              subtitle: this.subtitle,
              beforeTableHtml: `<h4 style="text-align: center">${this.selectedCompaniesNames}</h4>`,
            },
          ],
        });
      }
    },
  },
};
</script>
